import { type ComponentProps, useState } from 'react';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUp from '@mui/icons-material/VolumeUp';
import {
    Box,
    Fade,
    IconButton,
    Typography,
    Popover,
    Slider,
    styled,
    Tooltip,
    CircularProgress,
    Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const Container = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 12px;
    min-height: 34px;
    background: ${({ theme }) => theme.palette.info.main};
    color: ${({ theme }) => theme.palette.info.contrastText};
`;

const StyledCircularProgress = styled(CircularProgress)`
    color: ${({ theme }) => theme.palette.info.contrastText};
`;

const VolumePopover = styled(Popover)`
    & .MuiPopover-paper {
        width: 240px;
        max-width: 100%;
        height: 48px;

        display: flex;
        align-items: center;
        justify-content: center;
    }

    & .MuiSlider-root {
        width: 80%;
    }
`;

const ButtonsContainer = styled(Box)`
    margin-left: auto;
    display: flex;
`;

export interface VideoToolbarProps extends ComponentProps<typeof Box> {
    onVolumeUpdate?: ComponentProps<typeof Slider>['onChange'];
    onRefreshVideoState?: () => void | Promise<void>;
    isPaused?: boolean;
    currentVolume?: number;
}

export const VideoToolbar = (props: VideoToolbarProps) => {
    const { onVolumeUpdate, isPaused = false, currentVolume, children, onRefreshVideoState, ...other } = props;
    const { t } = useTranslation('playvideo');

    const [volumeAnchor, setVolumeAnchor] = useState<HTMLButtonElement | null>(null);
    const [waitingForRefresh, setWaitingForRefresh] = useState(false);

    const handleVolumeClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setVolumeAnchor(event.currentTarget);
    };

    const handleVolumeClose = () => {
        setVolumeAnchor(null);
    };

    const refreshVideo = async () => {
        setWaitingForRefresh(true);
        await onRefreshVideoState?.();
        setWaitingForRefresh(false);
    };

    const open = Boolean(volumeAnchor);
    const popoverId = open ? 'video-playback-volume' : undefined;

    return (
        <Container {...other}>
            <Fade in={isPaused}>
                <Tooltip title={t('playback-paused-tooltip')}>
                    <Chip icon={<PauseIcon />} size="small" label={t('playback-paused')} />
                </Tooltip>
            </Fade>
            <Typography variant="caption" flexGrow={1} textAlign="center">
                {children}
            </Typography>
            <ButtonsContainer>
                {onVolumeUpdate !== undefined && (
                    <Box>
                        <Tooltip title={t('playback-volume-tooltip')}>
                            <IconButton aria-describedby={popoverId} onClick={handleVolumeClick} size="small">
                                <VolumeUp />
                            </IconButton>
                        </Tooltip>
                        <VolumePopover
                            id={popoverId}
                            open={open}
                            anchorEl={volumeAnchor}
                            onClose={handleVolumeClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                        >
                            <Slider
                                value={currentVolume}
                                aria-label={t('playback-volume-aria')}
                                valueLabelDisplay="off"
                                min={0}
                                max={1}
                                step={0.05}
                                onChange={onVolumeUpdate}
                            />
                        </VolumePopover>
                    </Box>
                )}
                {onRefreshVideoState !== undefined && (
                    <Tooltip title={t('playback-refresh-tooltip')}>
                        <IconButton
                            aria-label={t('playback-refresh-aria')}
                            onClick={refreshVideo}
                            size="small"
                            disabled={waitingForRefresh}
                        >
                            {!waitingForRefresh && <AutorenewIcon />}
                            {waitingForRefresh && <StyledCircularProgress size={18} disableShrink />}
                        </IconButton>
                    </Tooltip>
                )}
            </ButtonsContainer>
        </Container>
    );
};
